import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
} from "react-native";
import { format } from "date-fns";
import {
  getOpportunitySuggestions,
  updateOpportunitySuggestionStatus,
} from "../../services/parseService";

interface Props {
  onReloadSuggestionCount: () => void;
}

const OpportunitySuggestionsScreen: React.FC<Props> = ({
  onReloadSuggestionCount,
}) => {
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSuggestions();
  }, []);

  const loadSuggestions = async () => {
    try {
      setIsLoading(true);
      const res = await getOpportunitySuggestions("Pending");
      setSuggestions(res);
      if (res.length) onReloadSuggestionCount();
    } catch (err) {
      setError("Failed to load suggestions.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatus = async (
    id: string,
    status: "Reviewed" | "Accepted" | "Rejected",
  ) => {
    try {
      await updateOpportunitySuggestionStatus(id, status);
      loadSuggestions();
    } catch (err) {
      Alert.alert("Error", "Failed to update suggestion.");
    }
  };

  const renderItem = ({ item }: { item: any }) => {
    const get = (key: string) => (item.get ? item.get(key) : item[key]);
    return (
      <View style={styles.card} data-oid="-a1j:ml">
        <View style={styles.header} data-oid="sq_iqy6">
          <Text style={styles.title} data-oid="hvyt2p.">
            {get("title")}
          </Text>
          <Text style={styles.date} data-oid="9uok:qf">
            {(() => {
              try {
                const raw = get("createdAt");
                const date =
                  raw instanceof Date ? raw : raw ? new Date(raw) : null;
                return date && !isNaN(date.getTime())
                  ? format(date, "MMM d, yyyy")
                  : "Unknown";
              } catch {
                return "Unknown";
              }
            })()}
          </Text>
        </View>
        <Text style={styles.description} data-oid="-4dxn3z">
          {get("description")}
        </Text>
        {get("link") && (
          <Text style={styles.link} data-oid="atsgveu">
            {get("link")}
          </Text>
        )}
        <View style={styles.actions} data-oid="91ja33l">
          <TouchableOpacity
            style={[styles.button, styles.accept]}
            onPress={() => handleStatus(item.id || item.objectId, "Accepted")}
            data-oid="t6v1mm4"
          >
            <Text style={styles.buttonText} data-oid="er7oau4">
              Accept
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.button, styles.review]}
            onPress={() => handleStatus(item.id || item.objectId, "Reviewed")}
            data-oid="-3-f3b-"
          >
            <Text style={styles.buttonText} data-oid="ci3lisj">
              Mark Reviewed
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.button, styles.reject]}
            onPress={() => handleStatus(item.id || item.objectId, "Rejected")}
            data-oid="y-694lu"
          >
            <Text style={styles.buttonText} data-oid="j0ifqt4">
              Reject
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  if (isLoading) {
    return (
      <View style={styles.center} data-oid="7:q1m8a">
        <ActivityIndicator size="large" color="#3182CE" data-oid="lyj.gum" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.center} data-oid="u7.j2sy">
        <Text data-oid="3d12xe2">{error}</Text>
      </View>
    );
  }

  return (
    <FlatList
      data={suggestions}
      keyExtractor={(item) => item.id || item.objectId}
      renderItem={renderItem}
      contentContainerStyle={{ padding: 16 }}
      data-oid="bg_4c4s"
    />
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#fff",
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2D3748",
    fontFamily: "Inter-SemiBold",
  },
  date: {
    fontSize: 14,
    color: "#718096",
    fontFamily: "Inter-Regular",
  },
  description: {
    fontSize: 14,
    color: "#4A5568",
    marginBottom: 8,
    fontFamily: "Inter-Regular",
  },
  link: {
    fontSize: 14,
    color: "#3182CE",
    marginBottom: 8,
    fontFamily: "Inter-Regular",
  },
  actions: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  button: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
  },
  buttonText: {
    color: "#fff",
    fontFamily: "Inter-SemiBold",
  },
  accept: { backgroundColor: "#10B981" },
  review: { backgroundColor: "#3182CE" },
  reject: { backgroundColor: "#E53E3E" },
  center: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
});

export default OpportunitySuggestionsScreen;
